import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import CrudTable from "@/components/CrudTable";
import { inr } from "@/lib/format";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

export default function BalanceSheet() {
  const { period } = useOutletContext();
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(() => {
    api.get(`/balance-summary?period=${period}`).then((r) => setSummary(r.data)).catch(() => {});
  }, [period]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const balColumns = [
    { key: "date", label: "Date" },
    { key: "flow", label: "Flow", type: "badge" },
    { key: "category", label: "Category" },
    { key: "amount", label: "Amount", type: "currency" },
    { key: "remarks", label: "Remarks" },
  ];
  const balFields = [
    { key: "date", label: "Date", type: "date", required: true },
    { key: "flow", label: "Flow", type: "select", options: ["input", "output"], required: true },
    { key: "category", label: "Category", type: "text", required: true },
    { key: "amount", label: "Amount (₹)", type: "number", required: true },
    { key: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  const cashColumns = [
    { key: "date", label: "Date" },
    { key: "revenue_collected", label: "Revenue", type: "currency" },
    { key: "total_expenses", label: "Expenses", type: "currency" },
    { key: "salaries_paid", label: "Salaries", type: "currency" },
    { key: "emi_paid", label: "EMI/Int", type: "currency" },
    { key: "tax_payments", label: "Tax", type: "currency" },
    { key: "purchases", label: "Purchases", type: "currency" },
    { key: "outstanding_receivables", label: "Receivables", type: "currency" },
    { key: "cash_position", label: "Cash Position", type: "signed" },
  ];
  const cashFields = [
    { key: "date", label: "Date", type: "date", required: true },
    { key: "revenue_collected", label: "Revenue Collected (₹)", type: "number" },
    { key: "total_expenses", label: "Total Expenses (₹)", type: "number" },
    { key: "salaries_paid", label: "Salaries Paid (₹)", type: "number" },
    { key: "emi_paid", label: "EMI / Interest (₹)", type: "number" },
    { key: "tax_payments", label: "Tax Payments (₹)", type: "number" },
    { key: "purchases", label: "Purchases (₹)", type: "number" },
    { key: "outstanding_receivables", label: "Outstanding Receivables (₹)", type: "number" },
    { key: "cash_position", label: "Cash Position (running ₹)", type: "number" },
    { key: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Balance Sheet Monitor</h1>
        <p className="text-slate-400 text-sm mt-1">Input vs Output at a glance with a running net position — always monitoring inflow against outflow.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="balance-health">
          <div className="rounded-xl border border-emerald-600/30 bg-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-4 text-emerald-400"><ArrowDownToLine className="h-4 w-4" /><span className="font-heading font-medium">Input (Money In)</span></div>
            <div className="space-y-2">
              {summary.inputs.map((i) => (
                <div key={i.category} className="flex justify-between text-sm"><span className="text-slate-400">{i.category}</span><span className="tabular-nums">{inr(i.amount)}</span></div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#334155] flex justify-between font-medium"><span>Total Input</span><span className="tabular-nums text-emerald-400">{inr(summary.total_input)}</span></div>
          </div>

          <div className="rounded-xl border border-rose-600/30 bg-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-4 text-rose-400"><ArrowUpFromLine className="h-4 w-4" /><span className="font-heading font-medium">Output (Money Out)</span></div>
            <div className="space-y-2">
              {summary.outputs.map((i) => (
                <div key={i.category} className="flex justify-between text-sm"><span className="text-slate-400">{i.category}</span><span className="tabular-nums">{inr(i.amount)}</span></div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#334155] flex justify-between font-medium"><span>Total Output</span><span className="tabular-nums text-rose-400">{inr(summary.total_output)}</span></div>
          </div>

          <div className="rounded-xl border border-[#C9A24B]/40 bg-[#1E293B] p-6 flex flex-col justify-center">
            <div className="label-caps">Net Position</div>
            <div className={`font-heading text-4xl font-bold tracking-tight mt-2 tabular-nums ${summary.net_position >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{inr(summary.net_position)}</div>
            <p className="text-xs text-slate-500 mt-2">{summary.net_position >= 0 ? "Healthy surplus this period." : "Deficit — inflow below outflow."}</p>
          </div>
        </div>
      )}

      <CrudTable
        testid="balance" endpoint="balance-entries"
        title="Input / Output Entries" subtitle={`Showing entries for the selected period. Log capital, revenue, income and investments (input) vs expenses, salaries, EMI, tax and liabilities (output).`}
        addLabel="Add Entry" columns={balColumns} fields={balFields}
        defaults={{ flow: "input", date: `${period}-15` }} onChanged={loadSummary}
        filterFn={(r) => String(r.date || "").slice(0, 7) === period}
      />

      <CrudTable
        testid="cashlog" endpoint="cash-log"
        title="Daily Cash Position Log" subtitle="Running daily cash position for the selected period, with receivables and payment breakdown."
        addLabel="Add Day" columns={cashColumns} fields={cashFields}
        defaults={{ date: `${period}-01` }}
        filterFn={(r) => String(r.date || "").slice(0, 7) === period}
      />
    </div>
  );
}
